import { useContext, useState } from 'react';
import { useCallback } from 'react';
import { HimmaContext } from '../context/HimmaContext';
import {
  DeleteSubAdminsRequest,
  ToggleSubAdminStatusRequest,
  WritableStoreDataSchema,
} from '../client-sdk/api';

export const useStoreApi = () => {
  const api = useContext(HimmaContext)!;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getAllStores = useCallback(
    async (query: any) => {
      setLoading(true);
      setError(null);
      try {
        // Only include search parameter if it has a value
        const searchParam =
          query.search && query.search.trim() !== '' ? query.search : undefined;
        const storeTypeParam =
          query.storeType && String(query.storeType).trim() !== '' ? query.storeType : undefined;
        const response = await api.getAllStoresForAdmin(
          query.page,
          query.limit,
          query.sortBy,
          query.sortOrder,
          query.statusFilter,
          query.addedByFilter,
          query.merchantId,
          storeTypeParam,
          searchParam
        );
        return response.data;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  const createStores = useCallback(
    async (subAdminData: WritableStoreDataSchema) => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.addStore(subAdminData);
        return response.data;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  const getToggleStatus = useCallback(
    async (query: any) => {
      setLoading(true);
      setError(null);
      try {
        const toggleStoreStatusRequest: ToggleSubAdminStatusRequest = {
          ids: Array.isArray(query.id) ? query.id : [query.id],
          status: query.status,
        };
        const response = await api.toggleStoreStatus(toggleStoreStatusRequest);
        return response.data;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  const deleteStore = useCallback(
    async (query: any) => {
      setLoading(true);
      setError(null);
      try {
        const deleteStore: DeleteSubAdminsRequest = {
          ids: query,
        };
        const response = await api.deleteStores(deleteStore);
        return response.data;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  const editStore = useCallback(
    async (id: any, subAdminData: WritableStoreDataSchema) => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.editStore(id, subAdminData);
        return response.data;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  return {
    getAllStores,
    createStores,
    getToggleStatus,
    deleteStore,
    editStore,
    loading,
    error,
  };
};
